import type { AhamoveJWTClaims } from './types';

/**
 * Decode JWT payload without verifying signature.
 * Browser-safe (uses atob), không cần lib ngoài.
 * KHÔNG dùng cho security verification — chỉ đọc claims để hiện UI optimistic.
 */
export function decodeJwtPayload(token: string): AhamoveJWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);

    let json: string;
    if (typeof atob !== 'undefined') {
      // atob returns Latin-1 string — convert to UTF-8 bytes then decode đúng
      // unicode (tránh mojibake với tên tiếng Việt như "Hoàng Nguyễn Huy").
      const binary = atob(padded);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      json = new TextDecoder('utf-8').decode(bytes);
    } else {
      json = Buffer.from(padded, 'base64').toString('utf-8');
    }
    return JSON.parse(json) as AhamoveJWTClaims;
  } catch {
    return null;
  }
}

/**
 * Return JWT exp claim (unix seconds). null nếu decode fail.
 */
export function decodeJwtExp(token: string): number | null {
  const claims = decodeJwtPayload(token);
  return claims?.exp ?? null;
}

/**
 * Check token còn valid theo exp local (chưa verify alive với BE).
 */
export function isJwtExpired(token: string, marginSec = 0): boolean {
  const exp = decodeJwtExp(token);
  if (!exp) return true;
  return Date.now() / 1000 >= exp - marginSec;
}

/**
 * Hash 16-char đầu của token để track sync state.
 * Không phải security hash — chỉ identify token unique.
 */
export async function hashToken(token: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = new TextEncoder().encode(token);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .slice(0, 8)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return token.slice(0, 16);
}
